import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Trash2 } from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDate } from "@/lib/format";
import type { AssessmentStatus, Language } from "@/types";

// ─────────────────────────────────────────────
// AssessmentTable
//
// Renders the assessments list DataTable.
// Columns: Entity Name, Schema, Status, Assigned To, Score, Progress, Last Updated.
// Design: Screen 5 — Assessments List §8.5
// ─────────────────────────────────────────────

export interface AssessmentRow {
  id: string;
  entityId: string;
  entityName: string;
  schemaName: string;
  status: AssessmentStatus;
  assignedTo: string | null;
  assignedToName?: string | null;
  completionPct: number;
  answered: number;
  total: number;
  overallScore: number | null;
  updatedAt: string;
  version: number;
}

interface AssessmentTableProps {
  data: AssessmentRow[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  page?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  canDelete?: boolean;
  onDelete?: (row: AssessmentRow) => void;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

function formatRelativeDate(iso: string, t: Translate, language: Language): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (min < 1) return t("assessment.justNow");
  if (min < 60) return t("assessment.minutesAgo", { n: min });
  if (hours < 24) return t("assessment.hoursAgo", { n: hours });
  if (days < 30) return t("assessment.daysAgo", { n: days });
  return formatDate(iso, language);
}

function buildColumns(t: Translate, language: Language): ColumnDef<AssessmentRow>[] {
  return [
    {
      key: "entityName",
      header: t("assessment.entity"),
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-[14px] font-medium text-text-primary">{row.entityName}</span>
          <span className="text-[12px] text-text-muted">v{row.version}</span>
        </div>
      ),
    },
    {
      key: "schemaName",
      header: t("assessment.schema"),
      cell: (row) => (
        <span className="text-[14px] text-text-secondary">{row.schemaName}</span>
      ),
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="assessment" status={row.status} />,
    },
    {
      key: "assignedTo",
      header: t("assessment.assignedColumn"),
      cell: (row) => {
        const assignee = row.assignedToName ?? row.assignedTo;
        return assignee ? (
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold text-text-primary"
              style={{ backgroundColor: "var(--accent-bg)" }}
            >
              {assignee.charAt(0).toUpperCase()}
            </span>
            <span className="text-[14px] text-text-secondary truncate max-w-[120px]">{assignee}</span>
          </div>
        ) : (
          <span className="text-[14px] text-text-muted">-</span>
        );
      },
    },
    {
      key: "score",
      header: t("assessment.score"),
      cell: (row) =>
        row.overallScore !== null && row.overallScore !== undefined ? (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-20 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${row.overallScore}%`,
                  backgroundColor: "var(--accent)",
                }}
              />
            </div>
            <span className="text-[12px] font-medium text-text-secondary">{Math.round(row.overallScore)}%</span>
          </div>
        ) : (
          <span className="text-[14px] text-text-muted">-</span>
        ),
    },
    {
      key: "progress",
      header: t("assessment.progress"),
      cell: (row) => (
        <span className="text-[13px] text-text-muted">
          {row.answered}/{row.total}
        </span>
      ),
    },
    {
      key: "updatedAt",
      header: t("assessment.lastUpdated"),
      sortable: true,
      cell: (row) => (
        <span className="text-[12px] text-text-muted">{formatRelativeDate(row.updatedAt, t, language)}</span>
      ),
    },
  ];
}

export function AssessmentTable({
  data,
  isLoading,
  isError,
  onRetry,
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  canDelete = false,
  onDelete,
}: AssessmentTableProps) {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const actionsColumn: ColumnDef<AssessmentRow> = {
    key: "actions",
    header: "",
    cell: (row) => (
      <div className="flex justify-end">
        <button
          type="button"
          title={t("assessment.deleteAssessmentTooltip")}
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(row);
          }}
          className="flex h-7 w-7 items-center justify-center rounded-control text-text-muted transition-colors hover:bg-danger-bg hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    ),
  };

  const columns = buildColumns(t, language);
  const resolvedColumns = canDelete ? [...columns, actionsColumn] : columns;

  return (
    <DataTable
      columns={resolvedColumns}
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onRowClick={(row) => navigate(`/assessments/${row.id}`)}
      emptyState={{
        icon: ClipboardCheck,
        title: t("assessment.noAssessments"),
        description: t("assessment.noAssessmentsDesc"),
      }}
      page={page}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
